import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  InputNumber,
  Select,
  Tabs,
  Tag,
  Tooltip,
  Alert,
  Space,
  Popover,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import { CalculatorOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import HolidayCalculator from "./HolidayCalculator";
import { buildExpectedWorkTime, validateExpectedWorkTime } from "../utils/expectedWorkTime";
import { useDispatch, useSelector } from "react-redux";
import { inviteUser } from "../redux/slieces/registrationSlice";
import { fetchLicenseStatus } from "../redux/slieces/licenseSlice";
import {
  getUsersInOrganisation,
  setUserInformation,
  deleteUser,
} from "../redux/slieces/userSlice";

import { useKeycloak } from "@react-keycloak/web";
import PaidOvertimeManagement from "./PaidOvertimeManagement";
import { PaidOvertimeService } from "../services/paidOvertimeService";
import looger from "../core/utils/logger";
import styles from "./EmployeeManagement.module.css";
import logger from "../core/utils/logger";
import "./EmployeeManagement.module.css";
import notificationBus from "../events/notificationBus";

import { useTranslation } from "react-i18next";
import { Spin } from "antd";

const EmployeeManagement = () => {
  // Translation hook - must be called before any early returns
  const { t } = useTranslation();
  
  // Keycloak context for authentication
  const { keycloak, initialized } = useKeycloak();
  
  // Check if user is authenticated and initialized
  if (!initialized) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" tip={t("loadingAuth")} />
      </div>
    );
  }
  
  // Check if user has admin role
  const isAdmin = keycloak.tokenParsed?.realm_access?.roles?.includes('mediadmin');
  
  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className={styles.accessDenied}>
        <h2>{t("accessDenied")}</h2>
        <p>{t("adminAccessRequired")}</p>
        <p>{t("contactAdministrator")}</p>
      </div>
    );
  }
  
  const [employees, setEmployees] = useState([]);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, index) => currentYear - index);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [holidaysByYear, setHolidaysByYear] = useState({});
  const [isHolidayCalcOpen, setIsHolidayCalcOpen] = useState(false);
  // Expected Weekly Work Time (Soll): per-user default + dated valid-from entries
  const [expectedWorkDefaultHours, setExpectedWorkDefaultHours] = useState(0);
  const [expectedWorkDefaultMinutes, setExpectedWorkDefaultMinutes] = useState(0);
  const [expectedWorkEntries, setExpectedWorkEntries] = useState([]);
  // Whether the edited user already had an expected-work-time setting. Used so
  // an untouched form doesn't clobber the "unset → fall back to template Soll".
  const [hadExpectedWorkTime, setHadExpectedWorkTime] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState("info");
  const [overtimeSummary, setOvertimeSummary] = useState({});
  const [inviteForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [inviteBlockedError, setInviteBlockedError] = useState(null);
  const dispatch = useDispatch();
  const { loading, error, users } = useSelector(
    (state) => state.userData.users
  );
  const licenseStatus = useSelector((state) => state.license?.status);
  const isLicenseOk = licenseStatus?.status === 'Ok';
  const isInviteAllowed = isAdmin && isLicenseOk;

  const adminOrgId =
    keycloak.tokenParsed && keycloak.tokenParsed.organisationId
      ? Array.isArray(keycloak.tokenParsed.organisationId)
        ? keycloak.tokenParsed.organisationId[0]
        : keycloak.tokenParsed.organisationId
      : null;
  logger.log("adminOrgId", adminOrgId);

  const fetchOvertimeSummary = async () => {
    if (!adminOrgId) return;
    try {
      const overtimeData = await PaidOvertimeService.getByOrganisation(adminOrgId);
      const summary = {};
      overtimeData.forEach((entry) => {
        if (!summary[entry.userId]) {
          summary[entry.userId] = { totalMinutes: 0 };
        }
        summary[entry.userId].totalMinutes += (entry.hours || 0) * 60 + (entry.minutes || 0);
      });
      setOvertimeSummary(summary);
    } catch (err) {
      logger.error("Error fetching overtime summary:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingEmployee(true);
      logger.log("Fetching users in organisation", loadingEmployee);
      await dispatch(getUsersInOrganisation());
      await fetchOvertimeSummary();
      setLoadingEmployee(false);
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLicenseStatus());
  }, [dispatch]);

  const KNOWN_LICENSE_STATUSES = ['Ok', 'NoLicense', 'Expired', 'LimitReached'];
  const KNOWN_BLOCKED_REASONS = ['NoLicense', 'Expired', 'LimitReached'];

  const renderLicenseBadge = () => {
    if (!licenseStatus) return null;
    const known = KNOWN_LICENSE_STATUSES.includes(licenseStatus.status);
    if (!known) {
      return (
        <Tag color="default" data-testid="license-count-badge">
          {t('license.invite.tooltip.unknown')}
        </Tag>
      );
    }
    if (licenseStatus.status === 'NoLicense') {
      return (
        <Tag color="default" data-testid="license-count-badge">
          {t('license.invite.badge.noLicense')}
        </Tag>
      );
    }
    const hasCounts =
      typeof licenseStatus.currentUsers === 'number' &&
      typeof licenseStatus.maxUsers === 'number';
    if (!hasCounts) {
      return (
        <Tag color="default" data-testid="license-count-badge">
          {t('license.invite.tooltip.unknown')}
        </Tag>
      );
    }
    const color =
      licenseStatus.status === 'Ok' ? 'green'
        : licenseStatus.status === 'LimitReached' ? 'orange'
        : 'red';
    return (
      <Tag color={color} data-testid="license-count-badge">
        {t('license.invite.badge.users', {
          current: licenseStatus.currentUsers,
          max: licenseStatus.maxUsers,
        })}
      </Tag>
    );
  };

  const inviteTooltipText = () => {
    if (!isAdmin) return t('license.invite.tooltip.adminsOnly');
    if (!licenseStatus) return t('license.invite.tooltip.loading');
    switch (licenseStatus.status) {
      case 'Ok':
        return '';
      case 'NoLicense':
        return t('license.invite.tooltip.noLicense');
      case 'Expired':
        return t('license.invite.tooltip.expired');
      case 'LimitReached':
        return t('license.invite.tooltip.limitReached');
      default:
        return t('license.invite.tooltip.unknown');
    }
  };

  const inviteBlockedMessage = () => {
    if (!inviteBlockedError) return null;
    const reason = KNOWN_BLOCKED_REASONS.includes(inviteBlockedError.reason)
      ? inviteBlockedError.reason
      : 'NoLicense';
    return t(`license.invite.blocked.message.${reason}`);
  };

  const showInviteModal = () => {
    setInviteBlockedError(null);
    setIsInviteModalVisible(true);
  };
  const handleInvite = () => {
    // Clear any prior license-blocked alert before re-validating; otherwise a validation
    // failure on the new attempt would leave the old 402 alert visible.
    setInviteBlockedError(null);
    inviteForm
      .validateFields()
      .then((values) => {
        const invitationPayload = {
          email: values.email,
          organizationId: adminOrgId,
        };
        logger.log("Invitation Payload in components:", invitationPayload);
        setLoadingEmployee(true);
        dispatch(inviteUser(invitationPayload))
          .unwrap()
          .then(() => {
            message.success(t("employeeInvited"));
            setLoadingEmployee(false);
            inviteForm.resetFields();
            setIsInviteModalVisible(false);
          })
          .catch((errorPayload) => {
            setLoadingEmployee(false);
            // 402 — license gate. Keep modal open, render inline alert, refresh licenseStatus.
            if (errorPayload?.httpStatus === 402) {
              setInviteBlockedError(errorPayload);
              dispatch(fetchLicenseStatus());
              return;
            }
            // Other errors: keep the modal open so the admin can retry without re-typing.
            const errMsg =
              typeof errorPayload?.message === 'string' && errorPayload.message
                ? errorPayload.message
                : t("inviteError");
            message.error(errMsg);
          });
      })
      .catch((info) => {
        logger.log(t("validateFailed"), info);
      });
  };

  const handleEdit = (record) => {
    setCurrentEmployee(record);
    setSelectedYear(currentYear);
    const initialHolidaysByYear = record.holidaysByYear || {};
    setHolidaysByYear(initialHolidaysByYear);
    // Seed Expected Weekly Work Time from the user's per-user setting (null = unset -> default 0/0)
    const ewt = record.expectedWorkTime || null;
    setHadExpectedWorkTime(!!ewt);
    setExpectedWorkDefaultHours(ewt?.defaultHours ?? 0);
    setExpectedWorkDefaultMinutes(ewt?.defaultMinutes ?? 0);
    setExpectedWorkEntries(
      (ewt?.entries || []).map((entry, index) => ({
        key: `${Date.now()}-${index}`,
        validFrom: entry.validFrom ? dayjs(entry.validFrom, "YYYY-MM-DD") : null,
        hours: entry.hours ?? 0,
        minutes: entry.minutes ?? 0,
      }))
    );
    editForm.setFieldsValue({
      title: record.title || '',
      firstName: record.firstName || '',
      surname: record.surname || '',
      position: record.position || '',
      remainingOvertime: record.remainingOvertime || 0,
      remainingHolidays: record.remainingHolidays || 0,
      holidaysByYear: initialHolidaysByYear,
    });
    setActiveEditTab("info");
    setIsEditModalVisible(true);
  };
  const handleSaveEdit = async() => {
    try {
      const values = await editForm.validateFields();

      // Validate & build Expected Weekly Work Time. Entries carry dayjs dates in
      // component state; normalise to "YYYY-MM-DD" strings for the pure helpers.
      const normalizedEntries = expectedWorkEntries.map((entry) => ({
        validFrom: entry.validFrom ? dayjs(entry.validFrom).format("YYYY-MM-DD") : null,
        hours: entry.hours,
        minutes: entry.minutes,
      }));
      const expectedWorkError = validateExpectedWorkTime({
        defaultHours: expectedWorkDefaultHours,
        defaultMinutes: expectedWorkDefaultMinutes,
        entries: normalizedEntries,
      });
      if (expectedWorkError) {
        message.error(t(expectedWorkError));
        return;
      }
      // Returns null (unset) when the user had none and the form is still empty,
      // so an untouched save never zeroes an employee's template-derived Soll.
      const expectedWorkTime = buildExpectedWorkTime({
        defaultHours: expectedWorkDefaultHours,
        defaultMinutes: expectedWorkDefaultMinutes,
        entries: normalizedEntries,
        hadExisting: hadExpectedWorkTime,
      });
      const updatedData = { ...currentEmployee, ...values, holidaysByYear, expectedWorkTime };

      setLoadingEmployee(true);
      await dispatch(setUserInformation(updatedData)).unwrap();
  
      message.success("User information updated");
      setIsEditModalVisible(false);

      // Re-fetch updated user list and overtime summary
      await dispatch(getUsersInOrganisation());
      await fetchOvertimeSummary();
    } catch (error) {
      message.error("Error updating user info: " + error.message);
    } finally {
      setLoadingEmployee(false);
    }
    
  };

  const handleCancelInvite = () => {
    setIsInviteModalVisible(false);
    inviteForm.resetFields();
    setInviteBlockedError(null);
  };
  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    fetchOvertimeSummary();
  };

  const handleDelete = async (id) => {
    try {
      setLoadingEmployee(true);
      await dispatch(deleteUser(id)).unwrap();
      message.success(t("employeeDeleted"));
    } catch (error) {
      message.error(t("deleteError") + ": " + (error.message || error));
      notificationBus.emit('notify', {
        type: 'error',
        message: error.message || error
      });
    } finally {
      setLoadingEmployee(false);
    }
  };

  const handleAcceptInvitation = (id) => {
    /*   setEmployees(employees.map(emp=>
                emp.id === id ? {...emp, status: 'Accepted'}: emp
            )); */
    message.success(t("userAccepted"));
  };
  //Filter employees based on search term
  const filteredEmployees = users.filter((emp) =>
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleHolidayChange = (value) => {
    const updated = { ...holidaysByYear, [selectedYear]: value };
    setHolidaysByYear(updated);
    editForm.setFieldsValue({ holidaysByYear: updated });
  };

  const handleAddExpectedWorkEntry = () => {
    setExpectedWorkEntries((prev) => [
      ...prev,
      { key: `${Date.now()}-${prev.length}`, validFrom: null, hours: 0, minutes: 0 },
    ]);
  };

  const handleRemoveExpectedWorkEntry = (key) => {
    setExpectedWorkEntries((prev) => prev.filter((entry) => entry.key !== key));
  };

  const handleExpectedWorkEntryChange = (key, field, value) => {
    setExpectedWorkEntries((prev) =>
      prev.map((entry) =>
        entry.key === key ? { ...entry, [field]: value } : entry
      )
    );
  };

  // Define the columns for the Ant Design Table
  const columns = [
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <>
          {t(text.toLowerCase())}
          {/* If invitation is pending, allow admin to simulate an acceptance */}
          {text === "Pending" && (
            <Button
              type="link"
              onClick={() => handleAcceptInvitation(record.id)}
            >
              {t("simulateAccept")}
            </Button>
          )}
        </>
      ),
    },
    {
      title: t("position"),
      dataIndex: "position",
      key: "position",
      render: (text) => text || '-',
    },
    {
      title: t("remainingOvertime"),
      dataIndex: "remainingOvertime",
      key: "remainingOvertime",
      render: (text) => text ?? 0,
    },
    {
      title: t("remainingHolidays"),
      dataIndex: "remainingHolidays",
      key: "remainingHolidays",
      render: (text) => text ?? 0,
    },
    {
      title: t("paidOvertime.paidOvertimeColumn"),
      key: "paidOvertime",
      render: (_, record) => {
        const data = overtimeSummary[record.id];
        if (!data || data.totalMinutes === 0) return "—";
        const h = Math.floor(data.totalMinutes / 60);
        const m = data.totalMinutes % 60;
        return `${h}h ${m}m`;
      },
    },
    {
      title: t("action"),
      key: "action",
      render: (text, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>
            {t("editInfo")}
          </Button>

          <Popconfirm
            title={t("confirmDelete")}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger type="link">
              {t("delete")}
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];
  const inviteTooltip = inviteTooltipText();
  const inviteButtonDisabled = !isInviteAllowed || loadingEmployee;
  const inviteButton = (
    <Button
      type="primary"
      onClick={showInviteModal}
      className="invite-button"
      style={{ marginRight: 10 }}
      disabled={inviteButtonDisabled}
      data-testid="invite-user-button"
    >
      {t("inviteUser")}
    </Button>
  );
  return (
    <div className={styles.employeeManagementContainer}>
      <Space style={{ marginBottom: 10 }} align="center">
        <h1 style={{ margin: 0 }}>{t("employeeManagement")}</h1>
        {renderLicenseBadge()}
      </Space>
      <div style={{ marginBottom: 10 }}>
        {inviteTooltip ? (
          <Tooltip title={inviteTooltip} placement="bottom">
            <span style={{ display: 'inline-block' }}>{inviteButton}</span>
          </Tooltip>
        ) : (
          inviteButton
        )}
      </div>
      <Input.Search
        placeholder={t("searchByEmail")}
        allowClear
        onSearch={(value) => setSearchTerm(value)}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ width: 200, marginBottom: "10px", marginLeft: 10 }}
      />
      { loadingEmployee ? (<div className={styles.loader}>
       <div className={styles.spinner}></div>
       <p>{t("loadingEmployees")}...</p>
     </div> ):(
      <div className={styles.customTable}>
        <Table dataSource={filteredEmployees} columns={columns} rowKey="id" />
      </div>)
      }

      <Modal
        title={t("modalTitle")}
        open={isInviteModalVisible}
        onOk={handleInvite}
        onCancel={handleCancelInvite}
        okText={t("sendInvitation")}
        okButtonProps={{ disabled: !isInviteAllowed || loadingEmployee }}
      >
        {inviteBlockedError && (
          <Alert
            data-testid="invite-blocked-alert"
            type="error"
            showIcon
            message={t('license.invite.blocked.title')}
            description={inviteBlockedMessage()}
            style={{ marginBottom: 12 }}
          />
        )}
        <Form form={inviteForm} layout="vertical">
          <Form.Item
            name={"email"}
            label={t("userEmail")}
            rules={[
              { required: true, message: t("emailRequired") },
              { type: "email", message: t("emailInvalid") },
            ]}
          >
            <Input placeholder={t("emailPlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>
      {/* Edit User Info Modal */}
      <Modal
        title={t("editUserInfo")}
        open={isEditModalVisible}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        okText={t("saveChanges")}
        width={640}
        footer={activeEditTab === "info" ? undefined : null}
      >
        <Tabs
          defaultActiveKey="info"
          onChange={setActiveEditTab}
          items={[
            {
              key: "info",
              label: t("paidOvertime.employeeInfoTab"),
              children: (
                <Form form={editForm} layout="vertical">
                  <Form.Item
                    name="title"
                    label={t("userTitle")}
                  >
                    <Select placeholder={t("selectTitle")} allowClear>
                      <Select.Option value="">{t("noTitle")}</Select.Option>
                      <Select.Option value="Dr.">Dr.</Select.Option>
                      <Select.Option value="Prof.">Prof.</Select.Option>
                      <Select.Option value="Prof. Dr.">Prof. Dr.</Select.Option>
                      <Select.Option value="Dr. med.">Dr. med.</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="firstName"
                    label={t("firstName")}
                  >
                    <Input placeholder={t("enterFirstName")} />
                  </Form.Item>
                  <Form.Item
                    name="surname"
                    label={t("surname")}
                  >
                    <Input placeholder={t("enterSurname")} />
                  </Form.Item>
                  <Form.Item
                    name="position"
                    label={t("position")}
                  >
                    <Input placeholder={t("positionPlaceholder")} />
                  </Form.Item>
                  <Form.Item
                    name="remainingOvertime"
                    label={t("remainingOvertimeHours")}
                    rules={[
                      { required: true, message: t("remainingOvertimeRequired") },
                    ]}
                  >
                    <InputNumber step={0.5} style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    name="remainingHolidays"
                    label={t("remainingHolidaysDays")}
                    rules={[
                      { required: true, message: t("remainingHolidaysRequired") },
                    ]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item label={t("year")}>
                    <Select value={selectedYear} onChange={setSelectedYear}>
                      {years.map((year) => (
                        <Select.Option key={year} value={year}>
                          {year}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label={t("holidaysForYear", { year: selectedYear })}>
                    <Space.Compact style={{ width: "100%" }}>
                      <InputNumber
                        min={0}
                        value={holidaysByYear[selectedYear] || 0}
                        onChange={handleHolidayChange}
                        style={{ width: "100%" }}
                      />
                      <Popover
                        trigger="click"
                        open={isHolidayCalcOpen}
                        onOpenChange={setIsHolidayCalcOpen}
                        title={t("holidayCalc.title")}
                        destroyTooltipOnHide
                        content={
                          isHolidayCalcOpen ? (
                            <HolidayCalculator
                              key={selectedYear}
                              year={selectedYear}
                              onApply={(total) => {
                                handleHolidayChange(total);
                                setIsHolidayCalcOpen(false);
                              }}
                            />
                          ) : null
                        }
                      >
                        <Button
                          type="text"
                          aria-label={t("holidayCalc.open")}
                          icon={<CalculatorOutlined />}
                        />
                      </Popover>
                    </Space.Compact>
                  </Form.Item>

                  {/* Expected Weekly Work Time (Soll): per-user default + dated valid-from changes */}
                  <Form.Item
                    label={`${t("expectedWeeklyWorkTime")} — ${t("expectedWorkTimeDefault")}`}
                  >
                    <Space.Compact style={{ width: "100%" }}>
                      <InputNumber
                        min={0}
                        max={168}
                        value={expectedWorkDefaultHours}
                        onChange={(value) => setExpectedWorkDefaultHours(value ?? 0)}
                        addonAfter={t("hours")}
                        style={{ width: "50%" }}
                      />
                      <InputNumber
                        min={0}
                        max={59}
                        value={expectedWorkDefaultMinutes}
                        onChange={(value) => setExpectedWorkDefaultMinutes(value ?? 0)}
                        addonAfter={t("minutes")}
                        style={{ width: "50%" }}
                      />
                    </Space.Compact>
                  </Form.Item>
                  {expectedWorkEntries.map((entry) => (
                    <Form.Item key={entry.key} label={t("validFrom")}>
                      <Space.Compact style={{ width: "100%" }}>
                        <DatePicker
                          format="YYYY-MM-DD"
                          value={entry.validFrom}
                          onChange={(date) =>
                            handleExpectedWorkEntryChange(entry.key, "validFrom", date)
                          }
                          style={{ width: "40%" }}
                        />
                        <InputNumber
                          min={0}
                          max={168}
                          value={entry.hours}
                          onChange={(value) =>
                            handleExpectedWorkEntryChange(entry.key, "hours", value ?? 0)
                          }
                          addonAfter={t("hours")}
                          style={{ width: "25%" }}
                        />
                        <InputNumber
                          min={0}
                          max={59}
                          value={entry.minutes}
                          onChange={(value) =>
                            handleExpectedWorkEntryChange(entry.key, "minutes", value ?? 0)
                          }
                          addonAfter={t("minutes")}
                          style={{ width: "25%" }}
                        />
                        <Button
                          type="text"
                          danger
                          aria-label={t("removeEntry")}
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveExpectedWorkEntry(entry.key)}
                        />
                      </Space.Compact>
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleAddExpectedWorkEntry}
                      style={{ width: "100%" }}
                    >
                      {t("addValidFromChange")}
                    </Button>
                  </Form.Item>

                  {/* Hidden field to store all years in the form */}
                  <Form.Item name="holidaysByYear" noStyle>
                    <Input type="hidden" />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "paidOvertime",
              label: t("paidOvertime.tabTitle"),
              children: currentEmployee ? (
                <PaidOvertimeManagement
                  userId={currentEmployee.id}
                  orgId={adminOrgId}
                  adminEmail={keycloak.tokenParsed?.email || ""}
                />
              ) : null,
            },
          ]}
        />
      </Modal>
    </div>
  );
};
export default EmployeeManagement;
